import { gql, useMutation } from '@apollo/client'
import { useNotification } from '@btime/react-messager'
import React from 'react'

import { templatesClient } from '../../../../utils/apollo/api'
import { usePermalinkToken } from '../../../../utils/session/permalinkToken'
import { usePrintPdf } from '../../../../components/pdf/options/_helpers/printPdf'
import { downloadFileFromUrl } from '../../../../utils/helpers/download'
import IconText from '../../../../packages/kiti/components/presentation/IconText'
import Link from '../../../../components/actions/Link'
import LoadingWrapper from '../../../../packages/kiti/components/helpers/LoadingWrapper'
import { Span } from '../../../../packages/kiti/components/presentation/Text'
import { Col, Grid } from '../../../../packages/kiti/components/structure/Grid'
import { useServiceOrder } from '../_load/ServiceOrderLoader'
import { buildDocumentModelForTemplate } from './documentModelRegistry'
import { EASYSOLUTIONS_BRAND, generateEasySolutionsDocxBlob } from './generateDocx'

// Mesma mutation/uso de `containers/templateRecords/TemplateRecordPdfPrintButton.js`
// (log/auditoria de geração de documento via `templateRecords`). Duplicada aqui, em
// vez de importada, para não alterar aquele arquivo compartilhado por ~25 marcas só
// para adicionar a opção DOCX exclusiva do Easy Solutions.
const MUTATION = gql`
  mutation UpsertTemplateRecord($input: TemplateRecordInput, $token: String) {
    upsertTemplateRecord(input: $input, serviceOrderToken: $token, forceReload: true) {
      id
    }
  }
`

const sanitizeFileName = name =>
  (name || 'documento')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9-_ ]/g, '')
    .trim()
    .replace(/\s+/g, '-')

const FormatOption = ({ label, active, onClick }) => (
  <Link color="white" onClick={onClick} underline={active} style={{ opacity: active ? 1 : 0.65 }}>
    <Span color="white" size="small" bold={active}>
      {label}
    </Span>
  </Link>
)

/**
 * Escolha PDF/DOCX + botão de geração, usada só nos 5 templates Easy
 * Solutions (ver `PageHeader.js`). Para PDF, reaproveita exatamente o fluxo
 * de impressão existente (`usePrintPdf`); para DOCX, monta o "document
 * model" do template (`_shared/documentModelRegistry.js`) e gera o .docx via
 * `_shared/generateDocx.js`, sem passar pelo `print-js`.
 */
const DocumentFormatButton = ({ name, recordId, templateId, noMargin }) => {
  const [format, setFormat] = React.useState('pdf')
  const [docxLoading, setDocxLoading] = React.useState(false)
  const token = usePermalinkToken()
  const notify = useNotification()
  const serviceOrder = useServiceOrder()
  const [printLoading, print] = usePrintPdf(name, noMargin)
  const [request, state] = useMutation(MUTATION, { refetchQueries: ['Users'], client: templatesClient })

  const generateDocx = async () => {
    setDocxLoading(true)
    try {
      const model = buildDocumentModelForTemplate(templateId, serviceOrder)
      if (!model) throw new Error(`Sem document model para templateId=${templateId}`)

      const blob = await generateEasySolutionsDocxBlob(model, EASYSOLUTIONS_BRAND)
      downloadFileFromUrl(URL.createObjectURL(blob), `${sanitizeFileName(name)}.docx`)
    } catch (err) {
      notify.error({ title: 'Não foi possível gerar o arquivo DOCX' })
    } finally {
      setDocxLoading(false)
    }
  }

  const handleGenerate = () => {
    const input = { recordId, templateId }

    return request({ variables: { input, token } }).then(resp => {
      if (resp.errors) {
        notify.error({ title: 'Ocorreu um erro ao gerar o arquivo' })
        return
      }

      return format === 'docx' ? generateDocx() : print()
    })
  }

  const loading = state.loading || printLoading || docxLoading

  return (
    <Grid gap={1} align="center" justify="flex-end">
      <Col>
        <Grid gap={0.4} align="center">
          <Col>
            <FormatOption label="PDF" active={format === 'pdf'} onClick={() => setFormat('pdf')} />
          </Col>
          <Col>
            <Span color="white" size="small">
              /
            </Span>
          </Col>
          <Col>
            <FormatOption label="DOCX" active={format === 'docx'} onClick={() => setFormat('docx')} />
          </Col>
        </Grid>
      </Col>
      <Col>
        <LoadingWrapper loading={loading} color="white">
          <Link onClick={handleGenerate} block>
            <IconText
              iconSize={1.4}
              weight="700"
              iconRight="BsFileEarmarkBreak"
              color="white"
              label={format === 'docx' ? 'Gerar DOCX' : 'Imprimir'}
            />
          </Link>
        </LoadingWrapper>
      </Col>
    </Grid>
  )
}

export default DocumentFormatButton
